import {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {Formik} from 'formik'
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Text,
  Progress,
  Flex,
  useDisclosure,
} from '@chakra-ui/react'
import {useTranslation} from 'react-i18next'
import {toast} from 'react-toastify'

import {useRails} from '~/react/contexts/rails'
import {ToastMessage} from '~/react/components/Toast'
import {CheckIcon, CrossIcon} from '~/react/icons'

export const BusinessConnectionDrawerWithSteps = ({
  isOpen,
  onClose,
  partner,
  onSuccess,
  apiFunction,
  steps,
  useFetchPrepopulationData,
  getValidationSchemaForStep,
  TermsAndConditionsModal,
}) => {
  const {t} = useTranslation('points')
  const {currentSupplier, authenticityToken} = useRails()

  const [currentStep, setCurrentStep] = useState(1)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    isOpen: isTermsModalOpen,
    onOpen: onOpenTermsModal,
    onClose: onCloseTermsModal,
  } = useDisclosure()

  const {
    prePopulationData: initialValues,
    fetch,
    isFetching,
  } = useFetchPrepopulationData()

  useEffect(() => {
    if (isOpen && !initialValues) {
      fetch()
    }
  }, [isOpen, initialValues, fetch])

  const totalSteps = steps.length

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleCancel = () => {
    if (isSubmitting) {
      return
    }
    setCurrentStep(1)
    onClose()
  }

  const onSuccessToast = (message) => {
    toast.dark(
      <ToastMessage>
        <CheckIcon mr={1} color="mld.success.700" />
        {message}
      </ToastMessage>,
    )
  }

  const onErrorToast = (message) => {
    toast.dark(
      <ToastMessage>
        <CrossIcon mr={1} color="mld.alert.700" />
        {message}
      </ToastMessage>,
    )
  }

  const handleSubmit = async (values) => {
    setIsSubmitting(true)

    const result = await apiFunction({
      supplierId: currentSupplier.id,
      authenticityToken,
      params: values,
    })

    if (result.error) {
      let errorMessage = t('points:connectionDrawer.error.generic')

      if (result.error.detail) {
        errorMessage = result.error.detail
      }

      onErrorToast(errorMessage)
    } else {
      onSuccessToast(t('points:connectionDrawer.success.connected'))
      onSuccess()
      handleCancel()
    }

    setIsSubmitting(false)
  }

  const getStepTitle = (step) => {
    const stepConfig = steps[step - 1]
    return stepConfig ? t(stepConfig.title) : ''
  }

  const renderStep = (formProps) => {
    const {isValid} = formProps
    const stepConfig = steps[currentStep - 1]

    if (!stepConfig) {
      return null
    }

    const StepComponent = stepConfig.component
    const stepProps = {
      isValid,
      isSubmitting,
      onCancel: handleCancel,
    }

    // Add navigation props based on step position
    if (currentStep === 1) {
      stepProps.onNext = handleNext
    } else if (currentStep === totalSteps) {
      stepProps.onSubmit = () => formProps.submitForm()
      stepProps.onBack = handleBack
      stepProps.onShowTermsModal = onOpenTermsModal
    } else {
      stepProps.onNext = handleNext
      stepProps.onBack = handleBack
    }

    return <StepComponent {...stepProps} />
  }

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={handleCancel} size="md">
      <DrawerOverlay />
      <DrawerContent>
        <TermsAndConditionsModal
          isOpen={isTermsModalOpen}
          onClose={onCloseTermsModal}
        />
        <DrawerCloseButton />
        <DrawerHeader>
          <Flex direction="column" gap="sm">
            <Text textStyle="heading-h4">
              {t('points:connectionDrawer.title', {
                partnerName: partner?.name,
              })}
            </Text>
            <Text textStyle="body-small" color="gray.600">
              {getStepTitle(currentStep)} ({currentStep} of {totalSteps})
            </Text>
            <Progress
              value={(currentStep / totalSteps) * 100}
              colorScheme="blue"
              size="sm"
            />
          </Flex>
        </DrawerHeader>

        <DrawerBody>
          {isFetching ? (
            <Text>{t('points:connectionDrawer.loading')}</Text>
          ) : (
            <Formik
              initialValues={initialValues}
              validationSchema={getValidationSchemaForStep(currentStep, t)}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {(formProps) => renderStep(formProps)}
            </Formik>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  )
}

const BusinessConnectionDrawer = ({
  isOpen,
  onClose,
  partner,
  onSuccess,
  apiFunction,
  FormComponent,
  useFetchPrepopulationData,
  getValidationSchema,
  TermsAndConditionsModal,
}) => {
  const {t} = useTranslation('points')
  const {currentSupplier, authenticityToken} = useRails()

  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    prePopulationData: initialValues,
    fetch,
    isFetching,
  } = useFetchPrepopulationData()

  useEffect(() => {
    if (!isOpen) {
      return
    }

    fetch()
  }, [isOpen, fetch])

  const {
    isOpen: isTermsModalOpen,
    onOpen: onOpenTermsModal,
    onClose: onCloseTermsModal,
  } = useDisclosure()

  const onSuccessToast = (message) => {
    toast.dark(
      <ToastMessage>
        <CheckIcon mr={1} color="mld.success.700" />
        {message}
      </ToastMessage>,
    )
  }

  const onErrorToast = (message) => {
    toast.dark(
      <ToastMessage>
        <CrossIcon mr={1} color="mld.alert.700" />
        {message}
      </ToastMessage>,
    )
  }

  const handleCancel = () => {
    if (isSubmitting) {
      return
    }
    onClose()
  }

  const handleSubmit = async (values) => {
    setIsSubmitting(true)

    const result = await apiFunction({
      supplierId: currentSupplier.id,
      authenticityToken,
      params: values,
    })

    if (result.error) {
      let errorMessage = t('points:connectionDrawer.error.generic')

      if (result.error.detail) {
        errorMessage = result.error.detail
      }

      onErrorToast(errorMessage)
    } else {
      onSuccessToast(t('points:connectionDrawer.success.connected'))
      onSuccess()
      handleCancel()
    }

    setIsSubmitting(false)
  }

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={handleCancel} size="md">
      <DrawerOverlay />
      <DrawerContent>
        <TermsAndConditionsModal
          isOpen={isTermsModalOpen}
          onClose={onCloseTermsModal}
          partner={partner}
        />
        <DrawerCloseButton />
        <DrawerHeader>
          <Flex direction="row" gap="sm">
            <Text textStyle="heading-h4">
              {t('points:connectionDrawer.title', {
                partnerName: partner?.name,
              })}
            </Text>
          </Flex>
        </DrawerHeader>

        <DrawerBody maxH="100%" display="flex" flexDirection="column">
          {isFetching ? (
            <Text>{t('points:connectionDrawer.loading')}</Text>
          ) : (
            <Formik
              initialValues={initialValues}
              validationSchema={getValidationSchema(t)}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {(formProps) => (
                <FormComponent
                  partner={partner}
                  memberStatus={initialValues?.memberStatus}
                  memberBusinessName={initialValues?.memberBusinessName}
                  abn={initialValues?.abn}
                  isValid={formProps.isValid}
                  isSubmitting={isSubmitting}
                  onCancel={handleCancel}
                  onSubmit={() => formProps.submitForm()}
                  onShowTermsModal={onOpenTermsModal}
                />
              )}
            </Formik>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  )
}

const BaseProps = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  partner: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
    mldPointRateFactor: PropTypes.number,
    externalPointRateFactor: PropTypes.number,
  }).isRequired,
  onSuccess: PropTypes.func.isRequired,
  apiFunction: PropTypes.func.isRequired,
  useFetchPrepopulationData: PropTypes.func.isRequired,
  TermsAndConditionsModal: PropTypes.elementType.isRequired,
}

BusinessConnectionDrawer.propTypes = {
  ...BaseProps,
  FormComponent: PropTypes.elementType.isRequired,
  getValidationSchema: PropTypes.func.isRequired,
}

BusinessConnectionDrawerWithSteps.propTypes = {
  ...BaseProps,
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      component: PropTypes.elementType.isRequired,
      title: PropTypes.string.isRequired,
    }),
  ).isRequired,
  getValidationSchemaForStep: PropTypes.func.isRequired,
}

export default BusinessConnectionDrawer
